import { FC, useState, useEffect, createContext, useContext, Dispatch, SetStateAction } from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, ProducerStatus, UserModel, UserProducerCenter } from './_models'
import * as authHelper from './AuthHelpers'
import { getUserByToken } from './_requests'
import { WithChildren } from '../../../../_metronic/helpers'
import { LogoutRequest } from './_requests'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  saveCurrentUser: (user) => void
  logout: () => void
  saveUserProducerCenter: (producer: UserProducerCenter) => void
  userProducerCenter: UserProducerCenter | undefined
  producerStatus: ProducerStatus
  setProducerStatus: Dispatch<SetStateAction<ProducerStatus>>
  updateUserInfo: (nationalCode: string) => void
}

const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: authHelper.getCurrentUser(),
  setCurrentUser: () => { },
  saveCurrentUser: () => { },
  logout: () => { },
  saveUserProducerCenter: () => { },
  userProducerCenter: authHelper.getUserProducerCenter(),
  producerStatus: authHelper.getUserProducerCenter()?.producerCenterId ? 'exists' : 'unknown',
  setProducerStatus: () => { },
  updateUserInfo: () => { }
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(authHelper.getCurrentUser())
  const [userProducerCenter, setUserProducerCenter] = useState<UserProducerCenter | undefined>(authHelper.getUserProducerCenter())
  const [producerStatus, setProducerStatus] = useState<ProducerStatus>(
    authHelper.getUserProducerCenter()?.producerCenterId ? 'exists' : 'unknown'
  )
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const saveCurrentUser = (user) => {
    setCurrentUser(user)
    authHelper.setCurrentUser(user)
  }

  const saveUserProducerCenter = (producer: UserProducerCenter) => {
    setUserProducerCenter(producer);
    setProducerStatus('exists')
    authHelper.setUserProducerCenter(producer)
  }

  const logout = async () => {
    const logoutRequest = await LogoutRequest()
    //if (logoutRequest.data?.isSuccess) {
    saveAuth(undefined)
    setCurrentUser(undefined)
    setUserProducerCenter(undefined)
    setProducerStatus('unknown')
    authHelper.removeCurrentUser()
    authHelper.removeUserProducerCenter()
    //}
  }

  const updateUserInfo = (nationalCode) => {
    setCurrentUser((prevUser) => {
      if (prevUser) {
        const updatedUser = { ...prevUser, nationalCode }; // Update nationalCode property
        authHelper.setCurrentUser(updatedUser); // Set the updated user in the authHelper
        return updatedUser;
      } else {
        return undefined; // No user exists
      }
    });
  }

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout, saveUserProducerCenter, userProducerCenter, producerStatus, setProducerStatus, updateUserInfo, saveCurrentUser }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, currentUser, logout, setCurrentUser } = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!currentUser) {
          const { data } = await getUserByToken(apiToken)
          if (data) {
            setCurrentUser(data)
            authHelper.setCurrentUser(data)
          }
          else {
            authHelper.removeCurrentUser()
          }
        }
      } catch (error) {
        console.error(error)
        if (currentUser) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }
    }

    if (auth && auth.token) {
      requestUser(auth.token)
    } else {
      // logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
